// TimePicker — compact two-select hour/minute control. Mirrors the styling
// of AddSupplementModal's ScheduleEditor but offers all 60 minutes (not just
// quarter-hour steps) since notification reminders care about precise times.

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

export function TimePicker({
  hour,
  minute,
  onChange,
  minuteStep = 5,
  ariaLabel,
}: {
  hour: number;
  minute: number;
  onChange: (hour: number, minute: number) => void;
  minuteStep?: number;
  ariaLabel?: string;
}) {
  const minuteOptions: number[] = [];
  for (let m = 0; m < 60; m += minuteStep) minuteOptions.push(m);
  // If the current minute isn't a step boundary (e.g. legacy 21:30 with step
  // 15), include it so we don't silently snap on first render.
  if (!minuteOptions.includes(minute)) minuteOptions.push(minute);
  minuteOptions.sort((a, b) => a - b);

  return (
    <div className="inline-flex items-center gap-1.5" aria-label={ariaLabel}>
      <select
        value={hour}
        onChange={(e) => onChange(Number(e.target.value), minute)}
        aria-label="Hour"
        className="rounded-control bg-bg-sunken border border-line px-2.5 py-2 text-sm font-mono tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
      >
        {Array.from({ length: 24 }).map((_, h) => (
          <option key={h} value={h}>
            {pad(h)}
          </option>
        ))}
      </select>
      <span className="font-mono text-fg-faint text-sm">:</span>
      <select
        value={minute}
        onChange={(e) => onChange(hour, Number(e.target.value))}
        aria-label="Minute"
        className="rounded-control bg-bg-sunken border border-line px-2.5 py-2 text-sm font-mono tabular-nums text-fg transition-colors focus:outline-none focus:border-accent focus:bg-bg-raised"
      >
        {minuteOptions.map((m) => (
          <option key={m} value={m}>
            {pad(m)}
          </option>
        ))}
      </select>
    </div>
  );
}

export function formatHM(hour: number, minute: number): string {
  return `${pad(hour)}:${pad(minute)}`;
}

export function parseHM(s: string): { hour: number; minute: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return { hour: h, minute: min };
}
